import React, { createContext, useState, useEffect } from 'react';
import api from '../api';
import Cookies from 'js-cookie';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Login function
  async function login(db, login, password) {
    setLoading(true);
    try {
      // Note: No leading slash for proxy compatibility
      const { data } = await api.post('api/login', { db, login, password });
      
      if (data && data.success) {
        // Store token in cookie with proper settings for persistence
        Cookies.set('quotes_token', data.token, {
          expires: 30, // 30 days
          path: '/', // Available across entire site
          secure: window.location.protocol === 'https:',
          sameSite: 'lax'
        });
        setUser(data.user);
        setIsAuthenticated(true);
      }
      return data;
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Connection error - please try again'
      };
    } finally {
      setLoading(false);
    }
  }

  // Request an OTP code be emailed to the given login
  async function requestOtp(db, login) {
    setLoading(true);
    try {
      // Note: No leading slash for proxy compatibility
      const { data } = await api.post('api/auth/otp/request', { db, login });
      return data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Connection error - please try again'
      };
    } finally {
      setLoading(false);
    }
  }

  // Verify an OTP code and establish a session
  async function verifyOtp(db, login, otpCode) {
    setLoading(true);
    try {
      // Note: No leading slash for proxy compatibility
      const { data } = await api.post('api/auth/otp/verify', { db, login, otp_code: otpCode });

      if (data && data.success) {
        // Store token in cookie with proper settings for persistence
        Cookies.set('quotes_token', data.access_token, {
          expires: 30, // 30 days
          path: '/', // Available across entire site
          secure: window.location.protocol === 'https:',
          sameSite: 'lax'
        });

        // The server also returns a refresh_token. It is deliberately not
        // stored: nothing consumes it yet, and a long-lived credential sitting
        // in a JS-readable cookie is attack surface an XSS could use to outlive
        // the access token. Add it back alongside a real silent-refresh caller.

        setUser(data.user);
        setIsAuthenticated(true);
      }
      return data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || 'Connection error - please try again'
      };
    } finally {
      setLoading(false);
    }
  }

  // Logout function
  async function logout() {
    setLoading(true);
    try {
      // Note: No leading slash for proxy compatibility
      const { data } = await api.post('api/logout');
      setUser(null);
      setIsAuthenticated(false);
      // Remove the correct cookie name
      Cookies.remove('quotes_token');
      return data || { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Logout failed'
      };
    } finally {
      setLoading(false);
    }
  }

  // Check session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const token = Cookies.get('quotes_token');

        if (!token) {
          setIsAuthenticated(false);
          setInitialized(true);
          return;
        }

        // Use GET method as defined in your Odoo backend
        // The api instance should already include the Authorization header
        const { data } = await api.get('api/session');

        if (data && data.success && data.user) {
          setUser(data.user);
          setIsAuthenticated(true);
        } else {
          // Invalid session, remove token
          setIsAuthenticated(false);
          Cookies.remove('quotes_token');
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Session check failed:', error);
        }
        setIsAuthenticated(false);
        // Remove invalid token
        Cookies.remove('quotes_token');
      } finally {
        setInitialized(true);
      }
    };

    checkSession();
  }, []);

  // Listen for unauthorized events dispatched by the API layer (e.g. 401 responses)
  // and clear auth state so protected routes redirect to login.
  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      setIsAuthenticated(false);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      requestOtp,
      verifyOtp,
      loading,
      initialized,
      isAuthenticated
    }}>
      {children}
    </AuthContext.Provider>
  );
}